import { useEffect, useRef, useState } from 'react';
import { strings } from '@/shared/strings';
import { RefreshIcon } from '@/ui/icons';
import { EXIT } from '@/ui/motion/motion';
import styles from './PullToRefresh.module.css';

/** How far the finger has to travel down, from the top of the list, before letting go refreshes. */
export const PULL_TRIGGER_PX = 80;
/** Movement below this is a tap or a wobble, not a pull. */
const SLOP_PX = 10;

/** The element the touch would scroll: the first ancestor that can scroll vertically. */
function scrollerOf(target: EventTarget | null): HTMLElement | null {
  let node = target instanceof HTMLElement ? target : null;
  while (node !== null && node !== document.body) {
    const overflow = getComputedStyle(node).overflowY;
    if ((overflow === 'auto' || overflow === 'scroll') && node.scrollHeight > node.clientHeight) return node;
    node = node.parentElement;
  }
  return document.scrollingElement instanceof HTMLElement ? document.scrollingElement : null;
}

/**
 * Pull down from the top of a screen to refresh it. Request of 4 October 2026,
 * section 8, made deliberate by the request that followed it, section 3.
 *
 * It counts only when the list under the finger is already at its very top,
 * the finger moves down and keeps moving down, and it is let go past
 * PULL_TRIGGER_PX. Scrolling up through a list never shows it: the list is not
 * at its top until the scroll has finished, and a touch that began with the list
 * scrolled is ignored to the end.
 *
 * It used to check `window.scrollY`, but the window never scrolls in this app:
 * the shell's body does. So the page always looked to be at its top, and every
 * downward swipe, which is how a list is scrolled up, set the spinner going.
 */
export function PullToRefresh({ onRefresh }: { onRefresh: () => Promise<unknown> }) {
  const [pull, setPull] = useState(0);
  const [busy, setBusy] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const start = useRef<{ y: number; scroller: HTMLElement | null } | null>(null);
  const distance = useRef(0);
  const refresh = useRef(onRefresh);
  refresh.current = onRefresh;

  useEffect(() => {
    const cancel = () => {
      start.current = null;
      distance.current = 0;
      setPull(0);
    };
    const down = (event: TouchEvent) => {
      const target = event.target as HTMLElement | null;
      // Sheets and dialogs scroll on their own, and have their own close gesture.
      if (target?.closest('[role="dialog"]') != null) return;
      const scroller = scrollerOf(target);
      if ((scroller?.scrollTop ?? 0) > 0) return;
      start.current = { y: event.touches[0]?.clientY ?? 0, scroller };
    };
    const move = (event: TouchEvent) => {
      const begun = start.current;
      if (begun === null) return;
      // The list moved after all: this is a scroll, not a pull.
      if ((begun.scroller?.scrollTop ?? 0) > 0) return cancel();
      const dy = (event.touches[0]?.clientY ?? begun.y) - begun.y;
      if (dy < 0) return cancel();
      if (dy < SLOP_PX) return;
      distance.current = dy;
      setPull(Math.min(dy, PULL_TRIGGER_PX * 1.5));
    };
    const up = () => {
      if (start.current === null) return;
      const pulled = distance.current;
      cancel();
      if (pulled >= PULL_TRIGGER_PX) {
        setBusy(true);
        // Settles away rather than vanishing: a short shrink and fade once done.
        void refresh.current().finally(() => {
          setLeaving(true);
          setTimeout(() => {
            setBusy(false);
            setLeaving(false);
          }, EXIT.normal);
        });
      }
    };
    window.addEventListener('touchstart', down, { passive: true });
    window.addEventListener('touchmove', move, { passive: true });
    window.addEventListener('touchend', up);
    window.addEventListener('touchcancel', cancel);
    return () => {
      window.removeEventListener('touchstart', down);
      window.removeEventListener('touchmove', move);
      window.removeEventListener('touchend', up);
      window.removeEventListener('touchcancel', cancel);
    };
  }, []);

  if (pull === 0 && !busy) return null;
  const ready = pull >= PULL_TRIGGER_PX;

  return (
    <div
      className={`${styles.pull} ${busy ? styles.busy : ''} ${ready ? styles.ready : ''} ${leaving ? styles.leaving : ''}`}
      style={{ transform: `translate(-50%, ${busy ? PULL_TRIGGER_PX * 0.5 : pull * 0.5}px)` }}
      role="status"
      aria-label={busy ? strings.freshness.updating : ready ? strings.freshness.release : strings.freshness.pull}
    >
      <RefreshIcon size={18} style={busy ? undefined : { transform: `rotate(${pull * 3}deg)` }} />
    </div>
  );
}
